import { ApplicationLogo, Chevron, User } from "bonza-shared/js"
import { Link, router, usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@headlessui/react"
import { Login as LoginIcon } from "bonza-shared/js"
import UserDropdown, {
    UserDropdownOption,
} from "@/components/molecules/UserNavDropdown/UserDropdown"
import { useLogger } from "bonza-shared"

export default function NavigationBar({
    user,
    overlap,
}: {
    user?: User
    overlap: boolean
}) {
    const { url } = usePage()
    const logger = useLogger("NavigationBar")

    const [dropdownOpen, setDropdownOpen] = useState(true)
    const [hidden, setHidden] = useState(false)

    useEffect(() => {
        setDropdownOpen(false)
    }, [url])

    const logoutCallback = () => {
        router.post(route("logout"), undefined, {
            onFinish: () => router.visit(route("web.home")),
            onError: (err) => {
                router.reload()
                logger.error(err)
            },
        })
    }

    useEffect(() => {
        const scrollContainer = document.querySelector("body")
        if (!scrollContainer) return
        let lastY = scrollContainer.scrollTop

        const onScroll = (e: Event) => {
            if (dropdownOpen) {
                e.preventDefault()
                return
            }
            const y = scrollContainer.scrollTop
            setHidden(y > lastY && y > 10)
            lastY = y
        }

        scrollContainer.addEventListener("scroll", onScroll)
        return () => scrollContainer.removeEventListener("scroll", onScroll)
    }, [dropdownOpen])

    const userDropdownOptions: UserDropdownOption[] = [
        {
            name: "Download Bonza",
            onClick: () => router.visit(route("web.download")),
            method: "get",
            as: "button",
        },
        {
            name: "Account Settings",
            onClick: () => router.visit(route("web.account")),
            method: "get",
            as: "button",
        },
        {
            name: "Log out",
            onClick: logoutCallback,
            method: "post",
            as: "button",
        },
    ]

    return (
        <div
            className={`
                top-0 z-[999] flex w-full items-center justify-center
                bg-gradient-to-b from-black/25 to-transparent tracking-[-2%]
                transition-all duration-300

                ${overlap ? "fixed" : "sticky"}
                ${hidden ? "-translate-y-full" : "translate-y-0"}
            `}
        >
            <nav
                className={`
                    px-main max-w-main flex w-full items-center justify-between
                    gap-8 py-4 transition-all duration-50

                    md:gap-16

                    ${dropdownOpen ? "bg-bonza-dark" : `bg-transparent`}

                    lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-0 lg:bg-transparent
                `}
            >
                <Link href={route("web.home")}>
                    <div className={"z-10 flex items-center pb-2"}>
                        <ApplicationLogo className={"h-auto w-32"} />
                    </div>
                </Link>
                <menu
                    className={`flex grow justify-end gap-8 lg:grow-0 lg:min-w-0`}
                >
                    <Button
                        onClick={() => setDropdownOpen((prev) => !prev)}
                        className={`
                            z-10 rounded-full px-6 py-4 transition-colors

                            hover:underline

                            lg:hidden

                            ${
                                dropdownOpen
                                    ? `
                                        hover:bg-bonza-pale
                                        hover:text-bonza-dark
                                    `
                                    : ``
                            }
                        `}
                    >
                        Menu
                    </Button>
                    <div
                        className={`
                            ${
                                dropdownOpen
                                    ? "opacity-100"
                                    : `invisible opacity-0`
                            }

                            absolute left-0 top-full flex max-h-dvh min-h-dvh
                            w-screen flex-col flex-nowrap
                            items-center gap-4 overflow-scroll px-4 pb-[100px]
                            bg-bonza-dark transition-all

                            lg:visible lg:static lg:left-auto lg:top-auto
                            lg:flex lg:h-auto lg:min-h-0 lg:w-auto lg:min-w-0
                            lg:flex-row lg:overflow-visible
                            lg:bg-transparent lg:pb-0 lg:opacity-100
                            lg:transition-none
                        `}
                    >
                        <ul
                            className={`
                                flex w-full list-none flex-col text-[25px]
                                font-bold

                                lg:flex-row lg:items-center
                                lg:gap-4 lg:text-[19px] lg:w-auto
                            `}
                        >
                            <div
                                className={`
                                    flex flex-col

                                    lg:flex-row lg:gap-4
                                `}
                            >
                                <NavDropDown
                                    name={`Who's it for`}
                                    items={[
                                        {
                                            route: "web.artists",
                                            name: "Artists",
                                            path: "/artists",
                                        },
                                        {
                                            route: "web.studios",
                                            name: "Studios",
                                            path: "/studios",
                                        },
                                        {
                                            route: "web.teachers",
                                            name: "Teachers",
                                            path: "/teachers",
                                        },
                                        {
                                            route: "web.education",
                                            name: "Educators",
                                            path: "/education",
                                        },
                                    ]}
                                />
                                <li className={""}>
                                    <NavLink
                                        item={{
                                            route: "web.plans",
                                            name: "Plans",
                                            path: "/plans",
                                        }}
                                    />
                                </li>
                                <li className={""}>
                                    <a
                                        className={`
                                            group block rounded-full px-2 pb-3
                                            pt-2 transition-colors

                                            lg:px-6 lg:pb-5 lg:pt-4
                                        `}
                                        href={
                                            "https://knowledgebase.bonzamusic.com"
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <span
                                            className={`
                                                border-b border-transparent
                                                transition-colors

                                                group-data-[active=true]:border-bonza-pale
                                                group-data-[active=true]:group-hover:border-bonza-pale

                                                group-hover:border-bonza-pale
                                            `}
                                        >
                                            Support
                                        </span>
                                    </a>
                                </li>

                                <li className={""}>
                                    <NavLink
                                        item={{
                                            route: "web.article",
                                            name: "Discover",
                                            path: "/discover",
                                        }}
                                    />
                                </li>
                            </div>
                            <div className={"lg:hidden"}>
                                <UserNavigation
                                    user={user}
                                    userDropdownOptions={userDropdownOptions}
                                />
                            </div>
                        </ul>
                    </div>
                </menu>
                <div
                    className={
                        "hidden lg:flex lg:items-center lg:justify-end lg:text-[19px] lg:font-bold"
                    }
                >
                    <UserNavigation
                        user={user}
                        userDropdownOptions={userDropdownOptions}
                    />
                </div>
            </nav>
        </div>
    )
}

function NavDropDown({
    name,
    items,
}: {
    name: string
    items: { path: string; name: string; route: string }[]
}) {
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const dropdownButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                dropdownButtonRef.current &&
                !dropdownButtonRef.current.contains(event.target)
            ) {
                setDropdownOpen(false)
            }
        }

        document.addEventListener("click", handleClickOutside)

        return () => {
            document.removeEventListener("click", handleClickOutside)
        }
    }, [])

    return (
        <li
            className={`
                group/dropdown relative flex items-stretch

                lg:items-center lg:justify-center
            `}
        >
            <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`
                    group block rounded-full transition-colors text-shadow

                    lg:px-2 lg:px-6 lg:pb-5 lg:pt-4 lg:hover:bg-transparent
                `}
                data-open={dropdownOpen}
                ref={dropdownButtonRef}
            >
                <div
                    className={`
                        flex hidden items-center justify-center gap-2
                        text-nowrap border-b border-transparent
                        transition-colors

                        group-data-[active=true]:border-bonza-pale
                        group-data-[active=true]:group-hover:border-transparent

                        group-hover:border-bonza-pale

                        lg:flex
                    `}
                >
                    {name}
                    <Chevron
                        className={`
                            size-4 rotate-90

                            data-[open=true]:-rotate-90
                        `}
                    />
                </div>
            </button>
            <ul
                className={`
                    flex flex-col rounded-[20px] transition-all duration-200

                    lg:absolute lg:-left-4 lg:top-full lg:max-h-0
                    lg:overflow-hidden lg:bg-bonza-dark lg:px-4
                    lg:data-[open=true]:max-h-[400px] lg:data-[open=true]:py-2
                `}
                data-open={dropdownOpen}
            >
                {items.map((item, i) => (
                    <li key={i} className={"text-left"}>
                        <NavLink
                            item={item}
                            className={`
                                text-nowrap p-0 px-0 pb-3 pt-2

                                lg:px-0
                            `}
                        />
                    </li>
                ))}
            </ul>
        </li>
    )
}

function NavLink({
    item,
    className,
}: {
    item: { path: string; name: string; route: string }
    className?: string
}) {
    const { url } = usePage()

    return (
        <Link
            data-active={url === item.path}
            className={`
                group block rounded-full px-2 transition-colors text-shadow pb-3
                pt-2

                lg:px-6 lg:pb-5 lg:pt-4 lg:hover:bg-transparent

                ${className}
            `}
            href={route(item.route)}
        >
            <span
                className={`
                    border-b border-transparent transition-colors

                    group-data-[active=true]:border-bonza-pale
                    group-data-[active=true]:group-hover:border-transparent

                    group-hover:border-bonza-pale
                `}
            >
                {item.name}
            </span>
        </Link>
    )
}

function UserNavigation({
    user,
    userDropdownOptions,
}: {
    user?: User
    userDropdownOptions: UserDropdownOption[]
}) {
    if (user) {
        return (
            <>
                <UserDropdown
                    options={userDropdownOptions}
                    user={user}
                    className={`
                        hidden

                        lg:flex
                    `}
                />
                <div
                    className={`
                        flex flex-col

                        lg:hidden
                    `}
                >
                    {userDropdownOptions.map((item, i) => (
                        <li key={i} className={""}>
                            <Button
                                onClick={item.onClick}
                                className={`
                                    group block rounded-full px-2 pb-3 pt-2
                                    transition-colors text-shadow w-full
                                    text-left

                                    lg:w-auto lg:px-6 lg:pb-5 lg:pt-4
                                    lg:text-center lg:hover:bg-transparent
                                `}
                            >
                                <span
                                    className={`
                                        border-b border-transparent
                                        transition-colors

                                        group-data-[active=true]:border-bonza-pale
                                        group-data-[active=true]:group-hover:border-transparent

                                        group-hover:border-bonza-pale
                                    `}
                                >
                                    {item.name}
                                </span>
                            </Button>
                        </li>
                    ))}
                </div>
            </>
        )
    } else {
        return (
            <div
                className={`
                    flex flex-col items-start px-2

                    lg:flex-row lg:items-center lg:justify-center lg:gap-2
                    lg:p-0
                `}
            >
                <div className={`self-stretch`}>
                    <Link
                        href={route("web.signupwithplan")}
                        className={`
                            group flex w-full items-center gap-2 text-nowrap
                            pb-3 pt-2 transition

                            lg:h-[64px] lg:h-full lg:w-auto lg:justify-center
                            lg:rounded-full lg:bg-bonza-dark lg:bg-opacity-10
                            lg:px-4 lg:pb-2 lg:pt-2 lg:hover:bg-bonza-pale
                            lg:hover:text-bonza-dark
                        `}
                    >
                        <span
                            className={`
                                border-b border-transparent transition-colors

                                group-hover:border-bonza-pale

                                lg:group-hover:border-bonza-pale
                            `}
                        >
                            Sign Up
                        </span>
                    </Link>
                </div>
                <div className={`self-stretch`}>
                    <Link
                        href={route("web.login")}
                        className={`
                            group flex w-full items-center rounded-full pb-3
                            pt-2 transition

                            lg:h-auto lg:w-auto lg:justify-center
                            lg:bg-bonza-dark lg:bg-opacity-10 lg:px-4 lg:pb-2
                            lg:pt-2 lg:hover:bg-bonza-pale
                            lg:hover:text-bonza-dark
                        `}
                        aria-label={"Login"}
                    >
                        <div
                            className={`
                                flex h-full items-center overflow-hidden lg:opacity-0
                                pb-0.5 pr-2 transition-all duration-150

                                lg:max-w-0 lg:pr-0 lg:group-hover:max-w-28 lg:group-hover:opacity-100
                                lg:group-hover:pr-2 lg:group-hover:duration-300
                            `}
                        >
                            <span
                                className={`
                                    text-nowrap border-b border-transparent
                                    transition-colors

                                    group-hover:border-bonza-pale
                                `}
                            >
                                Sign in
                            </span>
                        </div>
                        <div
                            className={`
                                hidden overflow-hidden transition-all duration-300

                                lg:flex lg:max-w-8 lg:items-center
                                lg:group-hover:max-w-0 lg:group-hover:duration-150
                            `}
                        >
                            <LoginIcon className={"h-5 w-5 shrink-0"} />
                        </div>
                    </Link>
                </div>
            </div>
        )
    }
}
